import CommonHeader from "components/organisms/CommonHeader";
import PatientSidebar from "components/organisms/PatientSidebar";
import React, { useMemo } from "react";
import styled from "styled-components";
import DashboardEmptyState from "./EmptyState";
import DashboardReport from "./DashboardReport";
import DashboardStatsCards from "./DashboardStatsCards";
import DashboardNhisSection from "./DashboardNhisSection";
import DashboardPinnedReports from "./DashboardPinnedReports";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { AIReview } from "common/models/types";

const TemplateContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: #f7fafd;
`;

const MainSection = styled.main`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const ContentSection = styled.section`
  flex: 1;
  height: calc(100vh - 103px);
  overflow: auto;
  padding: 32px 24px;
`;

const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ReportsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

interface DashboardTemplateProps {
  onPatientSelect: (patientId: number) => void;
  onSelectReport: (patientId: number, reportId: number) => void;
  onAddPatient: () => void;
}

export const DashboardTemplate: React.FC<DashboardTemplateProps> = ({
  onPatientSelect,
  onSelectReport,
  onAddPatient,
}) => {
  const { patientList, reviewHistory, reviews, pinnedReviews, pinnedReviewCount } = useSelector(
    (state: RootState) => state.data,
  );

  // 핀 보고서 상세 복원 + pinnedAt DESC 정렬 (고정한 시점 최신순)
  const pinnedReportDetails = useMemo<AIReview[]>(() => {
    if (!pinnedReviews) return [];
    const details = pinnedReviews
      .map((r) => reviews[r.id])
      .filter((r): r is AIReview => !!r);
    return [...details].sort((a, b) => {
      const aAt = String(a.pinnedAt ?? "");
      const bAt = String(b.pinnedAt ?? "");
      if (aAt !== bAt) return bAt.localeCompare(aAt);
      return (b.id ?? 0) - (a.id ?? 0);
    });
  }, [pinnedReviews, reviews]);

  // 최근 보고서: 백엔드 기본 정렬 id DESC 그대로 사용 (핀 여부 무관 최신순)
  const recentReports = useMemo<AIReview[]>(() => {
    if (!reviewHistory) return [];
    return reviewHistory
      .map((r) => reviews[r.id])
      .filter((r): r is AIReview => !!r);
  }, [reviewHistory, reviews]);

  const hasPatients = !!patientList && patientList.length > 0;

  return (
    <TemplateContainer>
      <CommonHeader />
      <MainSection>
        <PatientSidebar
          selectedPatientId={null}
          selectedReportId={null}
          onPatientSelect={onPatientSelect}
          onSelectReport={onSelectReport}
          onAddPatient={onAddPatient}
        />
        <ContentSection>
          {hasPatients ? (
            <SectionStack>
              <DashboardStatsCards />
              <DashboardNhisSection />
              {pinnedReportDetails.length > 0 ? (
                <ReportsRow>
                  <DashboardPinnedReports
                    reports={pinnedReportDetails}
                    totalCount={pinnedReviewCount ?? pinnedReportDetails.length}
                    onSelectReport={onSelectReport}
                  />
                  <DashboardReport
                    reviewList={recentReports}
                    reviews={reviews}
                    onSelectReport={onSelectReport}
                  />
                </ReportsRow>
              ) : (
                <DashboardReport
                  reviewList={recentReports}
                  reviews={reviews}
                  onSelectReport={onSelectReport}
                />
              )}
            </SectionStack>
          ) : (
            <DashboardEmptyState onAddPatient={onAddPatient} />
          )}
        </ContentSection>
      </MainSection>
    </TemplateContainer>
  );
};

export default DashboardTemplate;
